import React, { useCallback } from "react";
import HeaderPanel from "../layout/HeaderPanel";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { fetchExploredDocumentPartLocation } from "../../api/document/Location";
import { FileLocation } from "./FileLocationProvider";
import { WithApiFileLocationProvider } from "./WithApiFileLocationProvider";
import { DocumentLocationExplorer } from "./DocumentLocationExplorer";
import { FlatPathTreeItem } from "../generic/FlatPathTreeExplorer";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import FooterPanel from "../layout/FooterPanel";
import { exportDocumentRootAsync, getFormattedPath } from "../../redux/feature/document/document-slice";
import { useAppDispatch, useAppSelector } from "../../redux/hooks/hooks";
import {
  selectBackendUrl,
  selectClientId,
  selectCpmUsername,
  selectTenantId,
} from "../../redux/feature/settings/configurations-slice";
import { elaborationStatusItemIsBlocking } from "../../redux/feature/object/remote-elaborations-slice";
import { EVENT_CHANNEL_DEFAULT_TOPIC } from "../../utils/constants/eventChannel";
import { EXPORT_ELABORATION } from "../../utils/constants/notification";
import { ButtonVariants } from "../../styles/fluent/ButtonVariants";
import { ElaborationWrapper } from "../coobjects/ElaborationWrapper";
import { useFilteredElaborations } from "../generic/GenericElaborationSelector";

interface ExportProps {
  documentId: string;
  channelIdToNotify: string;
  onCloseModal: () => void;
}

interface DocumentListWrapperState {
  loaded: boolean;
  selectedTreeItem: FlatPathTreeItem | undefined;
}

const documentListWrapperInitialState: DocumentListWrapperState = {
  loaded: false,
  selectedTreeItem: undefined,
};
export const ExportDocumentPanel: React.FunctionComponent<ExportProps> = (props) => {
  const [state, setState] = React.useState<DocumentListWrapperState>(documentListWrapperInitialState);
  const username = useAppSelector(selectCpmUsername); // TEMP waiting for real login
  const backendUrl = useAppSelector(selectBackendUrl);
  const tenantId = useAppSelector(selectTenantId);
  const clientId = useAppSelector(selectClientId);
  const dispatch = useAppDispatch();
  const fetchItems = async (pathToFetch) => {
    setState((s) => ({ ...s, loaded: true }));
    const folders = await fetchExploredDocumentPartLocation(pathToFetch);
    return folders.map<FileLocation>((df) => ({
      key: df.name,
      text: df.name,
    }));
  };

  const exportDocument = (itemSelected: FlatPathTreeItem) => {
    exportDocumentRootAsync({
      documentId: props.documentId,
      sharepointPath: getFormattedPath(itemSelected?.path),
      username,
      tenantId,
      dispatch,
      backendUrl,
      clientId,
      channelIdToNotify: props.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
    }).then(() => {
      setState({
        ...state,
        loaded: false,
      });
      props.onCloseModal();
    });
  };

  function onSelectItem(item: FlatPathTreeItem | undefined) {
    setState({ ...state, selectedTreeItem: item });
  }

  const addedElaborationsDetails = useAppSelector((store) => store.remoteElaborations.addedElaborationItemsList);
  const blockingElabFindFunction = useCallback(
    (item) => item.elaborationType === "EXPORT" && elaborationStatusItemIsBlocking(item, addedElaborationsDetails),
    [addedElaborationsDetails]
  );
  const blockingElaboration = useFilteredElaborations({
    findFunction: blockingElabFindFunction,
  });
  return (
    <ElaborationWrapper notificationKey={EXPORT_ELABORATION} elaborationItem={blockingElaboration}>
      <>
        <HeaderPanel data-testid={"header"} applyPadding>
          {formatMessage("cof.file.open.export.title.selectfolder")}
        </HeaderPanel>
        <WithApiFileLocationProvider cleanState={!state.loaded} getItems={fetchItems} selectDepth="ITEM">
          <DocumentLocationExplorer onSelectItem={onSelectItem} page={"EXPORT"} />
        </WithApiFileLocationProvider>

        <FooterPanel>
          <PrimaryButton
            className={ButtonVariants.primary}
            data-testid={"export"}
            text={formatMessage("cof.base.export")}
            style={{ float: "right", marginLeft: "15px" }}
            onClick={() => exportDocument(state.selectedTreeItem)}
          />
          <PrimaryButton
            className={ButtonVariants.primary}
            text={formatMessage("cof.base.back")}
            onClick={props.onCloseModal}
            style={{
              float: "left",
            }}
          />
        </FooterPanel>
      </>
    </ElaborationWrapper>
  );
};
